



































import { Vue, Component, Prop } from 'vue-property-decorator';
import { getIdFromUrl, Youtube } from 'vue-youtube';

import CloseModalIcon from '@/components/icons/CloseModal.vue';
import FilledButton from '@/components/molecules/FilledButton/FilledButton.vue';

import eventBus from '@/utils/eventBus';
import segmentIo from '@/utils/segment.io';

type VideoProps = {
  duration: number;
  isPaused: boolean;
  isPlaying: boolean;
  progress: number;
};

let timer: NodeJS.Timeout;

@Component({
  components: {
    CloseModalIcon,
    Youtube,
    FilledButton
  }
})
export default class VideoTutorialModal extends Vue {
  @Prop() onVideoWatchedProgress?: (percentage: string) => void;

  @Prop(String) pageViewLabel?: string;
  @Prop(String) videoUrl!: string;
  @Prop(String) title!: string;

  watchedPercentage: Record<number, boolean> = {
    0: false,
    25: false,
    50: false,
    75: false,
    90: false
  };
  videoProps: VideoProps = {
    duration: 0,
    isPlaying: false,
    isPaused: true,
    progress: 0
  };

  get videoId() {
    return getIdFromUrl(this.videoUrl);
  }

  get playerVars() {
    return {
      modestbranding: 1,
      rel: 0
    };
  }

  created() {
    this.resizeEventHandler();
  }

  beforeDestroy() {
    Object.assign(document.body.style, {
      overscrollBehavior: 'auto'
    });

    window.removeEventListener('resize', this.resizeEventHandler);
  }

  listenVideoWatched() {
    timer = setInterval(async () => {
      const currentTime = await this.getVideoDuration();
      const progress = (currentTime * 100) / this.videoProps.duration;
      const roundedProgress = Math.floor(progress);

      const entries = Object.entries(this.watchedPercentage).map(
        ([percentageKey, value]) => [
          percentageKey,
          roundedProgress < Number(percentageKey) ? false : value
        ]
      );

      this.watchedPercentage = Object.fromEntries(entries);

      const firstValueNotCompleted = Object.entries(
        this.watchedPercentage
      ).find(([_, isCompleted]) => !isCompleted);

      const percentage =
        firstValueNotCompleted && Number(firstValueNotCompleted[0]);

      if (
        !isNaN(Number(percentage)) &&
        roundedProgress >= percentage! &&
        !this.watchedPercentage[percentage!]
      ) {
        this.watchedPercentage[percentage!] = true;

        if (this.onVideoWatchedProgress) {
          this.onVideoWatchedProgress(`${percentage}%`);
        }
      }

      this.videoProps = {
        ...this.videoProps,
        progress: roundedProgress
      };
    }, 1000); // 1s
  }

  stopListenVideoWatched() {
    if (timer) clearInterval(timer);
  }

  handleEnded() {
    const entries = Object.entries(this.watchedPercentage).map(
      ([percentageKey]) => [percentageKey, false]
    );

    this.watchedPercentage = Object.fromEntries(entries);

    this.stopListenVideoWatched();

    if (this.onVideoWatchedProgress) {
      this.onVideoWatchedProgress('100%');
    }
  }

  handlePaused() {
    this.videoProps = {
      ...this.videoProps,
      isPaused: true,
      isPlaying: false
    };

    this.stopListenVideoWatched();
  }

  handleReady(event: any) {
    this.videoProps = {
      ...this.videoProps,
      duration: event.getDuration()
    };
  }

  handlePlaying(event: any) {
    this.videoProps = {
      ...this.videoProps,
      isPaused: false,
      isPlaying: true
    };

    this.listenVideoWatched();
  }

  resizeEventHandler() {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty(
      '--inactivity-consumers-modal-vh',
      `${vh}px`
    );
  }

  mounted() {
    if (this.pageViewLabel) {
      segmentIo.modal(this.pageViewLabel);
    }

    document.onkeydown = this.handleOnkeydown;

    Object.assign(document.body.style, {
      overscrollBehavior: 'none'
    });

    window.addEventListener('popstate', () => {
      this.onClose();
    });
  }

  handleOnkeydown({ key }: KeyboardEvent) {
    if (key === 'Escape') {
      this.onClose();
    }
  }

  clearRoute() {
    this.$router.replace({
      query: {
        ...this.$route.query,
        modal: undefined
      }
    });
  }

  async getVideoDuration() {
    const yt = this.$refs.youtube as any;
    const currentTime = await yt.player.getCurrentTime();

    return currentTime;
  }

  onClose() {
    this.stopListenVideoWatched();
    this.clearRoute();

    eventBus.$emit('closeModal');
  }
}
